// Pure export functions. No DOM, no side effects — they return strings or
// blobs. The UI layer is responsible for triggering downloads.

import type { Appointment, FinderConfig } from './types';
import { sortAppointments } from './logic';
import { formatTime } from './time';

/** Whole state as JSON (config + appointments, pretty-printed). */
export function toJSON(config: FinderConfig, appointments: Appointment[]): string {
  return JSON.stringify(
    { config, appointments: sortAppointments(appointments) },
    null,
    2,
  );
}

/** RFC-4180-compatible CSV. Header: date;weekday;start;end;duration_min */
export function toCSV(appointments: Appointment[]): string {
  const lines: string[] = ['date;start;end;duration_min'];
  for (const a of sortAppointments(appointments)) {
    const duration = a.endMinutes - a.startMinutes;
    lines.push(`${a.date};${formatTime(a.startMinutes)};${formatTime(a.endMinutes)};${duration}`);
  }
  return lines.join('\r\n') + '\r\n';
}

/** Minimal valid iCalendar 2.0 with floating local time (no TZID). */
export function toICS(appointments: Appointment[], summary = 'Terminvorschlag'): string {
  const stamp = icsStamp(new Date());
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Slot by gotyou.at//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  for (const a of sortAppointments(appointments)) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:${a.id}@slot.gotyou.at`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${icsLocal(a.date, a.startMinutes)}`,
      `DTEND:${icsLocal(a.date, a.endMinutes)}`,
      `SUMMARY:${escapeICSText(summary)}`,
      'STATUS:TENTATIVE',
      'TRANSP:TRANSPARENT',
      'END:VEVENT',
    );
  }

  lines.push('END:VCALENDAR');

  // Folding lines longer than 75 octets is technically required, but our content
  // is short enough that we skip it. CRLF-terminated per RFC 5545.
  return lines.join('\r\n') + '\r\n';
}

/** Floating local-time format: yyyymmddThhmmss (no Z, no TZID). */
function icsLocal(dateIso: string, minutes: number): string {
  const [y, m, d] = dateIso.split('-');
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mm = String(minutes % 60).padStart(2, '0');
  return `${y}${m}${d}T${h}${mm}00`;
}

/** UTC stamp: yyyymmddThhmmssZ */
function icsStamp(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  return `${y}${m}${day}T${h}${mi}${s}Z`;
}

/** Escape commas, semicolons, backslashes, newlines per RFC 5545. */
function escapeICSText(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

export type ExportFormat = 'json' | 'csv' | 'ics';

export type ExportPayload = {
  filename: string;
  mime: string;
  content: string;
};

/** Build a download payload for the given format. */
export function buildExport(
  format: ExportFormat,
  config: FinderConfig,
  appointments: Appointment[],
): ExportPayload {
  switch (format) {
    case 'json':
      return {
        filename: 'slot-survey.json',
        mime: 'application/json;charset=utf-8',
        content: toJSON(config, appointments),
      };
    case 'csv':
      return {
        filename: 'slot-survey.csv',
        mime: 'text/csv;charset=utf-8',
        content: toCSV(appointments),
      };
    case 'ics':
      return {
        filename: 'slot-survey.ics',
        mime: 'text/calendar;charset=utf-8',
        content: toICS(appointments),
      };
  }
}
